import * as Sentry from '@sentry/vue';

export default defineNuxtPlugin((nuxtApp) => {
  const { vueApp } = nuxtApp;
  const appConfig = useRuntimeConfig();

  if (appConfig.public?.NODE_ENV !== 'development' && appConfig.public?.NODE_ENV !== 'dev') {
    Sentry.init({
      app: [vueApp],
      dsn: appConfig.public.SENTRY_DSN,
      environment: appConfig.public?.NODE_ENV || '',

      // Modern integrations for v10
      integrations: [
        Sentry.browserTracingIntegration({
          enableInp: true,
          traceFetch: true,
          traceXHR: true,
          traceNavigation: true,
        }),
      ],

      // Performance monitoring
      tracesSampleRate: Number.parseFloat(appConfig.public.SENTRY_PERFORMANCE_SAMPLE_RATE) || 0.1,
      tracePropagationTargets: ['localhost', /^https:\/\/www\.hostelworld\.com/],

      // Error sampling
      sampleRate: Number.parseFloat(appConfig.public.SENTRY_ERROR_SAMPLE_RATE) || 0.1,

      // Release tracking
      release: appConfig?.public?.RELEASE_VERSION || 'staticweb',

      // URL filtering
      allowUrls: [/^https?:\/\/(.*)\.hostelworld\.com/],
      denyUrls: [
        /graph\.facebook\.com/i, // Facebook flakiness
        /connect\.facebook\.net\/en_US\/all\.js/i, // Facebook blocked
        /eatdifferent\.com\.woopra-ns\.com/i, // Woopra flakiness
        /static\.woopra\.com\/js\/woopra\.js/i,

        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,

        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        /script\.hotjar\.com\//i,
        /www\.google-analytics\.com\//i,
        /consent\.trustarc\.com/i,
        /consent-pref\.trustarc\.com/i,
        /googletagmanager\.com/i,
      ],

      // Enhanced error filtering and context
      beforeSend(event, hint) {
        const err = hint?.originalException;
        if (err?.data?.isFeatureGate) {
          return null; // Only drop intentional feature-gate errors
        }

        // Enhanced error logging with more context
        if (event.exception) {
          console.error(`### [Sentry Exception]: ${hint.originalException?.message || 'Unknown error'}`, {
            exception: hint?.originalException,
            eventType: event?.type,
            level: event?.level,
            tags: event?.tags,
            user: event?.user,
            context: event?.contexts,
            breadcrumbs: event?.breadcrumbs,
          });
        }

        // Enhanced error filtering
        const errorMessage = hint.originalException?.message || '';
        const knownNoise = [
          'ResizeObserver loop limit exceeded',
          'Script error.',
          'Script error',
          'Network Error',
          'Failed to fetch',
          'The user aborted a request',
        ];

        if (knownNoise.some((noise) => errorMessage.includes(noise))) {
          return null; // Suppress known browser noise
        }

        // Filter out errors from third-party scripts
        if (hint.originalException?.stack) {
          const stack = hint.originalException.stack;
          const thirdPartyPatterns = [
            /chrome-extension:/,
            /moz-extension:/,
            /safari-extension:/,
            /chrome:\/\//,
            /moz-extension:\/\//,
            /safari-extension:\/\//,
          ];

          if (thirdPartyPatterns.some((pattern) => pattern.test(stack))) {
            return null;
          }
        }

        // Continue sending to Sentry
        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        // Add more context to breadcrumbs
        if (breadcrumb.category === 'navigation') {
          breadcrumb.data = {
            ...breadcrumb.data,
            currentUrl: window?.location?.href,
            referrer: document?.referrer,
          };
        }

        // Add user interaction context
        if (breadcrumb.category === 'ui.click' || breadcrumb.category === 'ui.input') {
          breadcrumb.data = {
            ...breadcrumb.data,
            elementId: breadcrumb.data?.target?.id,
            elementClass: breadcrumb.data?.target?.className,
            elementTag: breadcrumb.data?.target?.tagName,
          };
        }

        return breadcrumb;
      },

      // Performance monitoring
      enableTracing: true,
    });

    // Modern Vue error handling for v10
    Sentry.attachErrorHandler(vueApp, {
      logErrors: false, // We handle logging in beforeSend
      attachProps: true,
      trackComponents: true,
      timeout: 2000,
      hooks: ['activate', 'mount', 'update'],
    });

    const thirdPartyResourceUrl
      = /consent\.trustarc\.com|consent-pref\.trustarc\.com|googletagmanager\.com|www\.google-analytics\.com|script\.hotjar\.com/i;

    const isThirdPartyResourceError = (event) => {
      const target = event?.target;
      const targetTag = target?.tagName;
      if (targetTag === 'SCRIPT' || targetTag === 'LINK') {
        const url = target.src || target.href || '';
        return thirdPartyResourceUrl.test(url);
      }
      const filename = event?.filename || '';
      return !event?.error && thirdPartyResourceUrl.test(filename);
    };

    const isThirdPartyRejection = (reason) => {
      const message = reason?.message || String(reason || '');
      const stack = reason?.stack || '';
      return thirdPartyResourceUrl.test(message) || thirdPartyResourceUrl.test(stack);
    };

    // Global error handler for unhandled errors
    window.addEventListener('error', (event) => {
      if (isThirdPartyResourceError(event)) {
        return;
      }
      console.error('### [Unhandled Error]:', event?.error || event);
      Sentry.captureException(event?.error || event, {
        tags: {
          errorType: 'unhandled',
          source: 'window.error',
        },
        contexts: {
          browser: {
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });
    });

    // Global promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      if (isThirdPartyRejection(event?.reason)) {
        return;
      }
      console.error('### [Unhandled Promise Rejection]:', event?.reason || event);
      Sentry.captureException(event?.reason || event, {
        tags: {
          errorType: 'unhandledrejection',
          source: 'promise.rejection',
        },
        contexts: {
          browser: {
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });
    });

    // Performance monitoring for long tasks
    if ('PerformanceObserver' in window) {
      try {
        const sentryThreshold = Number.parseFloat(appConfig.public.SENTRY_THRESHOLD) || 50;
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > sentryThreshold) {
              // Tasks longer than the configured SENTRY_THRESHOLD
              Sentry.addBreadcrumb({
                category: 'performance',
                message: 'Long task detected',
                data: {
                  duration: entry.duration,
                  startTime: entry.startTime,
                  name: entry.name,
                },
                level: 'warning',
              });
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (error) {
        console.warn('### PerformanceObserver not supported:', error);
      }
    }

    return {
      provide: {
        sentrySetContext: Sentry.setContext,
        sentrySetUser: Sentry.setUser,
        sentrySetTag: Sentry.setTag,
        sentryAddBreadcrumb: Sentry.addBreadcrumb,
        sentryCaptureException: Sentry.captureException,
        sentryCaptureMessage: Sentry.captureMessage,
      },
    };
  }
});
